// packages/core/src/learning/schools/anti-fragility.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Anti-fragility". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview/exhibit names: nassim-taleb. (All canon
//                     entries are Taleb works or unattributed concepts; no
//                     other figures named.)
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const ANTI_FRAGILITY_EXHIBIT: SchoolExhibitEntry = {
	id: 'anti-fragility',
	tagline: 'Anti fragility believes some systems get stronger from stress.',
	worldviewSummary:
		'Anti fragility is paying attention to what breaks under pressure and what improves because of it. This lens protects you from building a life that only works when everything goes smoothly. It accepts redundancy, cash buffers, optionality, boring resilience, and small experiments because surprises are part of reality. The blind spot is using chaos as an excuse to be reckless. Anti fragility works best when the downside is survivable and the upside is real.',
	coreBeliefs: [
		'Anti fragility believes fragile plans depend on perfect conditions.',
		'Anti fragility believes buffers, redundancy, and options can make stress less destructive.',
		'Anti fragility believes small experiments can teach without threatening the whole system.',
		'Anti fragility believes upside matters only when the downside is survivable.',
		'Anti fragility believes comfort can hide weakness until pressure exposes it.',
		'Anti fragility believes overconfidence is dangerous when the world is uncertain.',
	],
	warnsAgainst: [
		'Building a money life that collapses after one surprise.',
		'Calling risk courage when the downside can ruin your base.',
		'Optimizing every dollar so tightly that there is no room for error.',
		'Treating chaos like a personality trait instead of a force to respect.',
	],
	helps: 'This lens helps people who want more resilience, more optionality, and better preparation for uncertain income, surprise expenses, job changes, moves, markets, or life shocks. It may not fit when someone uses it to justify unnecessary risk, constant disruption, or avoidable instability.',
	figures: [],
	canon: [
		{
			title: 'Antifragile',
			author: 'Nassim Nicholas Taleb',
			oneLineIdea:
				'Some systems do more than survive stress. They improve when exposed to disorder in controlled ways.',
		},
		{
			title: 'The Black Swan',
			author: 'Nassim Nicholas Taleb',
			oneLineIdea:
				'Rare events can change everything, so plans should not assume the future will behave neatly.',
		},
		{
			title: 'Fooled by Randomness',
			author: 'Nassim Nicholas Taleb',
			oneLineIdea:
				'People often mistake luck for skill when outcomes are noisy.',
		},
		{
			title: 'Barbell strategy',
			oneLineIdea:
				'Keep the base safe while taking small, limited experiments where the upside can be meaningful.',
		},
		{
			title: 'Optionality',
			oneLineIdea:
				'Options matter because they let you respond when the future changes.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-anti-fragility',
	debateIds: [],
	applyActions: [
		{
			label: 'Check how much runway protects you from a surprise',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'See where your current money picture is fragile',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Test a safer base with a small upside experiment',
			action: {
				type: 'openSimulator',
				label: 'Simulate the experiment',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Name one fragile part of your plan',
			action: {
				type: 'noToolAction',
				label: 'Spot the weak point',
				prompt: 'Write one part of your plan that depends on everything going right, then name one buffer or option that would make it sturdier.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'A fragile money plan only works when life behaves.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of maximum efficiency is having no room for surprises.',
			kind: 'hook',
		},
		{
			line: 'What part of your life looked strong until pressure tested it?',
			kind: 'interview-question',
		},
		{
			line: 'When did a setback accidentally make you more prepared?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people test whether their plan survives stress before life tests it for them.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'This is financial education for uncertainty, not perfect spreadsheet weather.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience the difference between being resilient and being reckless.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone what buffer saved them when income, health, or housing changed.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
